<script>
import Vue from 'vue';
import {mapState, mapActions, mapGetters} from 'vuex';
import {required} from "../../services/ValidatorService";
import AdminEditModalFormAccept from '../AdminEditModalFormAccept/AdminEditModalFormAccept.vue';

export default Vue.extend({
    components: {
        AdminEditModalFormAccept
    },
    filters: {
        roleFilter(role) {
            return role.description.slice(role.name.length, role.description.length).toUpperCase();
        }
    },
    data() {
        return {
            action: 'update',
        }
    },
    computed: {
        ...mapState({
            user: state => state.admin.editModal.user,
            originalUser: state => state.admin.editModal.originalUser,
            languages: state => state.admin.languages.languages,
            roles: state => state.admin.roles.roles,
            originalRoles: state => state.admin.roles.original
        }),
        ...mapGetters({
            userChanged: 'admin/userChanged'
        }),
        firstNameClasses() {
            return {
                'admin-edit-modal-form__input--error': this.$v.user.firstName.$dirty && this.$v.user.firstName.$error,
                'admin-edit-modal-form__input--success': this.$v.user.firstName.$dirty && !this.$v.user.firstName.$error
            }
        },
        lastNameClasses() {
            return {
                'admin-edit-modal-form__input--error': this.$v.user.lastName.$dirty && this.$v.user.lastName.$error,
                'admin-edit-modal-form__input--success': this.$v.user.lastName.$dirty && !this.$v.user.lastName.$error
            }
        },
        languageClasses() {
            return {
                'admin-edit-modal-form__dropdown--error': this.$v.user.language.$dirty && this.$v.user.language.$error,
                'admin-edit-modal-form__dropdown--success': this.$v.user.language.$dirty && !this.$v.user.language.$error
            }
        },
    },
    methods: {
        ...mapActions({
            updateUserValues: 'admin/updateEditUserValues',
            editUser: 'admin/editUser',
            blockUser: 'admin/blockUser',
            unblockUser: 'admin/unblockUser',
            deleteUser: 'admin/deleteUser',
            submitEditUserForm: 'admin/submitEditUserForm'
        }),
        update(obj) {
            this.$v.$touch();
            this.updateUserValues(obj);
        },
        submit(action) {
            this.action = action;
            this.submitEditUserForm();
        },
        accept() {
            switch(this.action) {
            case 'update':
                this.editUser(this.user);
                break;
            case 'block':
                this.blockUser(this.user.id);
                break;
            case 'unblock':
                this.unblockUser(this.user.id);
                break;
            case 'delete':
                this.deleteUser(this.user.id);
                break;
            }
        },
        hasRole(role) {
            let contains = false;
            this.user.roles.forEach((userRole) => {
                if(userRole.id === role.id) {
                    contains = true;
                }
            });
            return contains;
        },
        editRoles($event, role) {
            let newRoles = [];
            if ($event.target.checked) {
                newRoles.push(role);
            }
            this.user.roles.forEach((userRole) => {
                if(userRole.id !== role.id) {
                    newRoles.push(userRole);
                }
            });
            this.update({roles: newRoles});
        }
    },
    validations() {
        return {
            user: {
                firstName: {
                    required: (firstName) => {
                        return required(firstName, 'First name').valid;
                    }
                },
                lastName: {
                    required: (lastName) => {
                        return required(lastName, 'Last name').valid;
                    }
                },
                language: {
                    required: (language) => {
                        return required(language, 'Language').valid;
                    }
                }
            }
        }
    }
});
</script>
<template>
    <transition name="fade" mode="out-in">
        <div class="admin-edit-modal-form">
            <div v-if="user.status !== 'inactive' && user.status !== 'blocked'" class="admin-edit-modal-form__info">
                {{ $t('admin.edit.form.info') }}
            </div>
            <div v-if="user.status === 'inactive' || user.status === 'blocked'" class="admin-edit-modal-form__alert">
                {{ $t('admin.edit.form.warning.' + user.status) }}
            </div>
            <div v-if="user" class="admin-edit-modal-form__form">
                <div class="admin-edit-modal-form__label">{{ $t('admin.edit.form.firstname') }}</div>
                <input :class="firstNameClasses"
                       :value="user.firstName"
                       class="admin-edit-modal-form__input"
                       type="text"
                       @input="update({firstName: $event.target.value})">
                <transition name="fade" mode="out-in">
                    <span v-if="$v.user.firstName.$error" class="admin-edit-modal-form__error-msg">
                        {{ $t('admin.edit.form.validator.firstname') }}
                    </span>
                </transition>
                <div class="admin-edit-modal-form__label">{{ $t('admin.edit.form.lastname') }}</div>
                <input :class="lastNameClasses"
                       :value="user.lastName"
                       class="admin-edit-modal-form__input"
                       type="text"
                       @input="update({lastName: $event.target.value})">
                <transition name="fade" mode="out-in">
                    <span v-if="$v.user.lastName.$error" class="admin-edit-modal-form__error-msg">
                        {{ $t('admin.edit.form.validator.lastname') }}
                    </span>
                </transition>
                <div class="admin-edit-modal-form__label">{{ $t('admin.edit.form.language') }}</div>
                <select :value="user.language"
                        :class="languageClasses"
                        class="admin-edit-modal-form__input-dropdown"
                        @input="update({language: $event.target.value})">
                    <option v-for="(language, index) in languages" :value="index" :key="index">{{ language }}</option>
                </select>
                <div class="admin-edit-modal-form__roles">
                    <div class="admin-edit-modal-form__label">{{ $t('admin.edit.form.roles') }}</div>
                    <div class="admin-edit-modal-form__checkbox-group">
                        <div v-for="role in roles" :key="role.id" class="admin-edit-modal-form__checkbox-element">
                            <label class="admin-edit-modal-form__checkbox-label">{{ role | roleFilter }}</label>
                            <input :checked="hasRole(role)" class="admin-edit-modal-form__checkbox" type="checkbox" @click="editRoles($event, role)">
                        </div>
                    </div>
                </div>
                <div class="admin-edit-modal-form__btn-container">
                    <div class="admin-edit-modal-form__btn-group">
                        <button :disabled="!userChanged || $v.$error || !user.roles.length" class="admin-edit-modal-form__btn admin-edit-modal-form__btn-update" @click="submit('update')">
                            {{ $t('admin.edit.form.buttons.update') }}
                        </button>
                        <button v-if="user.status !== 'blocked'"
                                :disabled="user.status === 'inactive'"
                                class="admin-edit-modal-form__btn admin-edit-modal-form__btn-block"
                                @click="submit('block')">{{ $t('admin.edit.form.buttons.block') }}</button>
                        <button v-if="user.status === 'blocked'"
                                :disabled="user.status === 'inactive'"
                                class="admin-edit-modal-form__btn admin-edit-modal-form__btn-unblock"
                                @click="submit('unblock')">{{ $t('admin.edit.form.buttons.unblock') }}</button>
                        <button class="admin-edit-modal-form__btn admin-edit-modal-form__btn-delete" @click="submit('delete')">
                            {{ $t('admin.edit.form.buttons.delete') }}
                        </button>
                    </div>
                </div>
            </div>
            <admin-edit-modal-form-accept :action="action" @accept="accept"/>
        </div>
    </transition>
</template>



// WEBPACK FOOTER //
// src/components/AdminEditModalForm/AdminEditModalForm.vue